import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PackageOpen, Lock, Store } from "lucide-react";
import { API } from "@/lib/api";
import { zl } from "@/lib/shop";
import { shopApi, useShopAuth } from "@/lib/shopAuth";
import RedfoxLayout from "./RedfoxLayout";
import { Button } from "@/components/ui/button";

export default function MyStore() {
  const auth = useShopAuth();
  const [stores, setStores] = useState(null);
  const [active, setActive] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!auth?.ready) return;
    if (!auth.customer) { setLoading(false); return; }
    shopApi.get("/b2b/my/stores").then((r) => {
      setStores(r.data);
      if (r.data.length) setActive(r.data[0]);
      else setLoading(false);
    }).catch(() => { setStores([]); setLoading(false); });
  }, [auth?.ready, auth?.customer]);

  useEffect(() => {
    if (!active) return;
    setLoading(true);
    shopApi.get(`/b2b/my/stores/${active.id}/products`).then((r) => setProducts(r.data)).catch(() => setProducts([])).finally(() => setLoading(false));
  }, [active]);

  if (auth?.ready && !auth.customer) {
    return (
      <RedfoxLayout>
        <div className="mx-auto max-w-md px-4 py-24 text-center">
          <Lock className="mx-auto h-10 w-10 text-neutral-500 mb-4" />
          <h1 className="text-xl font-bold mb-2">Zaloguj się, aby zobaczyć swój sklep</h1>
          <p className="text-neutral-400 mb-6 text-sm">Dostęp do prywatnego sklepu wymaga logowania na przypisane konto.</p>
          <Link to="/sklep/logowanie"><Button className="bg-red-600 hover:bg-red-500 text-white font-semibold">Zaloguj się</Button></Link>
        </div>
      </RedfoxLayout>
    );
  }

  if (stores && stores.length === 0) {
    return (
      <RedfoxLayout>
        <div className="mx-auto max-w-md px-4 py-24 text-center" data-testid="redfox-no-store">
          <Store className="mx-auto h-10 w-10 text-neutral-500 mb-4" />
          <h1 className="text-xl font-bold mb-2">Brak przypisanego sklepu</h1>
          <p className="text-neutral-400 text-sm">Twoje konto nie ma jeszcze dostępu do żadnego prywatnego sklepu. Skontaktuj się z EMPRO.</p>
        </div>
      </RedfoxLayout>
    );
  }

  return (
    <RedfoxLayout store={active}>
      {/* Baner kolekcji */}
      {active?.banner_file_id ? (
        <div className="relative w-full overflow-hidden bg-neutral-900">
          <img src={`${API}/files/${active.banner_file_id}`} alt="" className="block w-full h-auto" />
        </div>
      ) : (
        <div className="relative w-full overflow-hidden py-16 sm:py-24" style={{ background: "linear-gradient(120deg,#0a0a0b 0%,#1a0608 60%,#0a0a0b 100%)" }}>
          <div className="pointer-events-none absolute inset-0 opacity-40" style={{ backgroundImage: "repeating-linear-gradient(115deg, rgba(228,18,42,0.45) 0 3px, transparent 3px 30px)" }} />
          <div className="pointer-events-none absolute -left-12 top-0 h-full w-44 -skew-x-12" style={{ background: "#E4122A", opacity: 0.9 }} />
          <div className="pointer-events-none absolute -right-20 bottom-0 h-24 w-96 skew-x-12" style={{ background: "linear-gradient(90deg,transparent,#E4122A)", opacity: 0.55 }} />
          <div className="relative mx-auto max-w-6xl px-6">
            <span className="inline-block bg-[#E4122A] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.22em] text-white" style={{ clipPath: "polygon(0 0,100% 0,92% 100%,0% 100%)" }}>Oficjalny sklep</span>
            <h1 className="mt-4 uppercase italic leading-[0.92] text-5xl sm:text-7xl" style={{ fontFamily: "'Oswald',sans-serif", fontWeight: 700 }}>{active?.name || "Kolekcja"}</h1>
            <p className="mt-4 text-neutral-400 text-sm uppercase tracking-[0.2em]">Kolekcja przygotowana dla zespołu</p>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-6xl px-4 py-10">
        {stores && stores.length > 1 && (
          <div className="mb-6 flex flex-wrap gap-2" data-testid="redfox-store-switcher">
            {stores.map((s) => (
              <button key={s.id} data-testid={`redfox-store-${s.id}`} onClick={() => setActive(s)}
                className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${active?.id === s.id ? "bg-red-600 text-white" : "bg-white/5 text-neutral-300 hover:text-white"}`}>{s.name}</button>
            ))}
          </div>
        )}

        {loading ? (
          <p className="text-neutral-500 py-20 text-center">Ładowanie…</p>
        ) : products.length === 0 ? (
          <div className="grid place-items-center py-24 text-neutral-500" data-testid="redfox-empty-products">
            <PackageOpen className="h-12 w-12 mb-4" />
            <p className="font-semibold">Brak produktów w sklepie</p>
            <p className="text-sm">Kolekcja pojawi się wkrótce.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="redfox-product-grid">
            {products.map((p) => (
              <Link key={p.id} to={`/sklep/moj-sklep/produkt/${p.id}`} data-testid={`redfox-product-${p.id}`}
                className="group relative block overflow-hidden rounded-xl bg-neutral-900 ring-1 ring-white/5 transition-all hover:ring-red-600/60 hover:-translate-y-1">
                {p.is_test && <span data-testid={`redfox-test-badge-${p.id}`} className="absolute left-2 top-2 z-10 rounded bg-amber-400 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-black">TEST</span>}
                <div className="aspect-[4/5] overflow-hidden bg-neutral-800 p-2">
                  {p.photos?.[0]
                    ? <img src={`${API}/files/${p.photos[0]}`} alt={p.name} className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-105" />
                    : <div className="grid h-full place-items-center text-neutral-600"><PackageOpen className="h-10 w-10" /></div>}
                </div>
                <span className="absolute left-0 bottom-0 h-0.5 w-0 bg-red-600 transition-all duration-300 group-hover:w-full" style={{ bottom: "76px" }} />
                <div className="p-3 sm:p-4">
                  <p className="font-semibold leading-tight line-clamp-2">{p.name}</p>
                  {p.subtitle && <p className="mt-0.5 text-xs text-neutral-400 line-clamp-1">{p.subtitle}</p>}
                  <p className="mt-2 text-xl tracking-wide" style={{ fontFamily: "'Oswald',sans-serif", fontWeight: 700 }}>{zl(p.price_grosz)}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </RedfoxLayout>
  );
}
